import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Database, ChevronRight, Trash2, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { personalizedTSService, PersonalizedTSMapping } from '../../../services/PersonalizedTSService';
import styles from './DataLibrary.module.css';
import logger from '../../../logging/logger';

interface DataLibraryProps {
  onClose?: () => void;
}

export const DataLibrary: React.FC<DataLibraryProps> = ({ onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mappings, setMappings] = useState<PersonalizedTSMapping[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingName, setDeletingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { userUuid, getToken } = useAuth();

  const fetchMappings = useCallback(async () => {
    if (!userUuid) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) return;

      const response = await personalizedTSService.listMappings(userUuid, token);
      setMappings(response.mappings || []);
    } catch (err) {
      logger.error('Failed to fetch mappings:', err);
      setError('Failed to load data library');
    } finally {
      setIsLoading(false);
    }
  }, [userUuid, getToken]);

  // Fetch mappings when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchMappings();
    }
  }, [isOpen, fetchMappings]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleDelete = async (e: React.MouseEvent, name: string) => {
    e.stopPropagation();
    if (!userUuid || deletingName) return;

    setDeletingName(name);
    try {
      const token = await getToken();
      if (!token) return;

      await personalizedTSService.deleteMapping(userUuid, token, name);
      setMappings(prev => prev.filter(m => m.qts_name !== name));
    } catch (err) {
      logger.error('Failed to delete mapping:', err);
      setError('Failed to delete');
    } finally {
      setDeletingName(null);
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button className={styles.header} onClick={handleToggle}>
        <Database className="h-4 w-4" />
        <span className={styles.label}>Data Library</span>
        <ChevronRight className={styles.chevron} size={14} />
      </button>

      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>Data Library</span>
          </div>
          <div className={styles.panelContent}>
            {isLoading ? (
              <div className={styles.loadingState}>
                <Loader2 className={`${styles.spinner} h-4 w-4`} />
                <span>Loading...</span>
              </div>
            ) : error ? (
              <div className={styles.errorState}>{error}</div>
            ) : mappings.length === 0 ? (
              <div className={styles.emptyState}>No saved data</div>
            ) : (
              <div className={styles.list}>
                {mappings.map((mapping) => (
                  <div key={mapping.qts_name} className={styles.item}>
                    <span className={styles.itemName} title={mapping.qts_name}>
                      {mapping.qts_name}
                    </span>
                    <button
                      className={styles.deleteButton}
                      onClick={(e) => handleDelete(e, mapping.qts_name)}
                      disabled={deletingName === mapping.qts_name}
                      title="Delete"
                    >
                      {deletingName === mapping.qts_name ? (
                        <Loader2 className={`${styles.spinner} h-3 w-3`} />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataLibrary;

